import { FAST_API_URL, postOptions } from "../index";
import type { OverlapPush } from "./types";
import type { VoicingCheck } from "../../providers/AudioProvider/types";

interface VoiceoverV2InspectionParams {
    mediaId: string;
    folderNum?: number;
    languageFrom?: string;
    languageTo?: string;
    debug?: boolean;
}

interface VoiceoverV2BaseProcessParams {
    mediaId: string;
    folderNum: number;
    languageFrom: string;
    languageTo: string;
    retryCount: number;
    mode: VoiceoverV2Mode;
    debug?: boolean;
    geminiModel?: VoiceoverV2GeminiModel;
}

interface VoiceoverV2FragmentProcessParams extends VoiceoverV2BaseProcessParams {
    metasubtitleId: string;
    signal?: AbortSignal;
    /** ♪ самой реплики: снятую с озвучки реплику вернуть и озвучить (как `unskip` у /synt_audio). */
    unskip?: boolean;
}

export interface VoiceoverV2FolderProcessParams extends VoiceoverV2BaseProcessParams {
    signal?: AbortSignal;
}

export type VoiceoverV2Mode = "check_only" | "autofix" | "force_regenerate";
export type VoiceoverV2SegmentStatus = "passed" | "failed" | "skipped" | "no_data";
export type VoiceoverV2GeminiModel =
    | "gemini-3-pro-preview"
    | "gemini-3.1-pro-preview"
    | "gemini-3.1-flash-lite-preview"
    | "gemini-3.1-flash-live-preview"
    | "gemini-3-flash-preview";

export interface VoiceoverV2CheckResult {
    passed?: boolean;
    skipped?: boolean;
    reason?: string;
    score?: number;
}

export interface VoiceoverV2InspectionSegment {
    chunk_id: string;
    folder_num: number;
    lang: string;
    language_from: string;
    status: VoiceoverV2SegmentStatus;
    passed: boolean;
    skipped: boolean;
    score: number | null;
    start: number;
    requires_manual_review: boolean;
    fail_count: number;
    attempts_made: number;
    captions: string;
    reason: string;
}

export interface VoiceoverV2InspectionOverviewResponse {
    segments_by_folder: Record<string, Record<string, VoiceoverV2InspectionSegment[]>>;
    total_segments: number;
    total_passed: number;
    total_failed: number;
    total_skipped: number;
    total_no_data: number;
    folders_with_segments: number[];
    folders_with_failures: number[];
}

export interface VoiceoverV2FragmentProcessResponse {
    status: string;
    message?: string;
    passed: boolean;
    reason?: string[] | string;
    last_error?: string | null;
    quality_check?: VoiceoverV2CheckResult | null;
    intonation_drop_check?: VoiceoverV2CheckResult | null;
    /** Пара «Без автоправок»: толчки, которыми сервер снял наложения после озвучки (см. OverlapPush). */
    pushed?: OverlapPush[];
    /** Короткий текст: итог проверки дубля на слух (см. VoicingCheck); нет поля — не проверялся. */
    voicing_check?: VoicingCheck;
}

export interface VoiceoverV2FolderResultItem {
    id: string;
    result: VoiceoverV2FragmentProcessResponse;
}

export interface VoiceoverV2FolderProcessResponse {
    status: string;
    results: VoiceoverV2FolderResultItem[];
}

const resolveVoiceoverV2Debug = (debug?: boolean) => {
    if (typeof debug === "boolean") {
        return debug;
    }

    if (typeof window === "undefined") {
        return false;
    }

    return new URLSearchParams(window.location.search).has("debug");
};

export const getDefaultVoiceoverV2GeminiModel = (): VoiceoverV2GeminiModel =>
    "gemini-3-pro-preview";

const buildInspectionUrl = (
    endpoint: string,
    { mediaId, folderNum, languageFrom, languageTo, debug }: VoiceoverV2InspectionParams,
) => {
    const params = new URLSearchParams({ media: mediaId });
    if (folderNum != null) {
        params.set("folder_num", String(folderNum));
    }
    if (languageFrom) {
        params.set("language_from", languageFrom);
    }
    if (languageTo) {
        params.set("language_to", languageTo);
    }
    if (resolveVoiceoverV2Debug(debug)) {
        params.set("debug", "true");
    }
    return `${FAST_API_URL}${endpoint}?${params.toString()}`;
};

const getErrorMessage = async (response: Response, fallback: string) => {
    try {
        const data = await response.json();
        if (typeof data === "string" && data.trim()) {
            return data;
        }
        // Объект причины ({reason, message} наших 409/422) — его слова, а не «[object Object]».
        if (typeof data?.detail?.message === "string") {
            return data.detail.message;
        }
        if (data?.detail) {
            return String(data.detail);
        }
        if (data?.message) {
            return String(data.message);
        }
    } catch (error) {
        try {
            const text = await response.text();
            if (text.trim()) {
                return text;
            }
        } catch {
            return fallback;
        }
    }

    return fallback;
};

export const getVoiceoverV2InspectionApi = async (
    params: VoiceoverV2InspectionParams,
): Promise<VoiceoverV2InspectionOverviewResponse> => {
    const response = await fetch(
        buildInspectionUrl("/voiceover_v2/inspection_segments", params),
    );

    if (!response.ok) {
        throw new Error(await getErrorMessage(response, "Failed to load Voiceover V2 inspection"));
    }

    return await response.json();
};

export const getVoiceoverV2FailedInspectionApi = async (
    params: VoiceoverV2InspectionParams,
): Promise<VoiceoverV2InspectionOverviewResponse> => {
    const response = await fetch(
        buildInspectionUrl("/voiceover_v2/failed_inspection_segments", params),
    );

    if (!response.ok) {
        throw new Error(await getErrorMessage(response, "Failed to load failed Voiceover V2 segments"));
    }

    return await response.json();
};

export const processVoiceoverV2FragmentApi = async ({
    mediaId,
    folderNum,
    languageFrom,
    languageTo,
    metasubtitleId,
    retryCount,
    mode,
    signal,
    debug,
    geminiModel,
    unskip,
}: VoiceoverV2FragmentProcessParams): Promise<VoiceoverV2FragmentProcessResponse> => {
    const response = await fetch(
        `${FAST_API_URL}/voiceover_v2/fragment?media=${encodeURIComponent(mediaId)}`,
        {
            ...postOptions,
            signal,
            body: JSON.stringify({
                folder_num: folderNum,
                language_from: languageFrom,
                language_to: languageTo,
                metasubtitle_id: metasubtitleId,
                retry_count: retryCount,
                mode,
                debug: resolveVoiceoverV2Debug(debug),
                gemini_model: geminiModel || getDefaultVoiceoverV2GeminiModel(),
                ...(unskip ? { unskip: true } : {}),
            }),
        },
    );

    if (!response.ok) {
        throw new Error(await getErrorMessage(response, "Failed to process Voiceover V2 fragment"));
    }

    return await response.json();
};

export const processVoiceoverV2FolderApi = async ({
    mediaId,
    folderNum,
    languageFrom,
    languageTo,
    retryCount,
    mode,
    signal,
    debug,
    geminiModel,
}: VoiceoverV2FolderProcessParams): Promise<VoiceoverV2FolderProcessResponse> => {
    const response = await fetch(
        `${FAST_API_URL}/voiceover_v2/folder?media=${encodeURIComponent(mediaId)}`,
        {
            ...postOptions,
            signal,
            body: JSON.stringify({
                folder_num: folderNum,
                language_from: languageFrom,
                language_to: languageTo,
                retry_count: retryCount,
                mode,
                debug: resolveVoiceoverV2Debug(debug),
                gemini_model: geminiModel || getDefaultVoiceoverV2GeminiModel(),
            }),
        },
    );

    if (!response.ok) {
        throw new Error(await getErrorMessage(response, "Failed to process Voiceover V2 folder"));
    }

    return await response.json();
};
